import jwt from '@/utils/jwt';
import { getAuthUser } from '@/store/auth/api';

/**
 * Send the member into the learner portal, already signed in.
 *
 * Asks core for a one-time handoff URL. On any failure — core down, no learner
 * profile, a malformed response — falls back to the portal's dispatch page
 * with a null token, so the member lands on the portal login rather than a
 * dead page. If the portal URL itself is not configured, there is nowhere
 * safe to send the member, so this logs a warning and leaves them where they
 * are instead of navigating to a 404 on this site.
 *
 * The mint route is /v1/users/{userId}/accesses/learn-portal, matching AUS.
 * `userId` is the raw key, kept in localStorage by store/auth/index.js from the
 * auth-user fetch (or fetched here, see resolveUserId). Without one there is
 * nothing to mint against, so this goes straight to the fallback rather than
 * requesting /v1/users/null/...
 *
 * `newTab: true` opens the tab AFTER an awaited network round trip, by which
 * point the user-gesture exemption has lapsed — browsers will usually block
 * it as a popup. This helper does not work around that (see
 * `resources/assets/js/mixins/learningCardMixin.js` and
 * `resources/assets/js/components/course/learningSearch.vue` for the
 * synchronous pattern this needs instead): a caller that needs a new tab
 * should open a blank one synchronously in its own click handler
 * (`const tab = window.open('', '_blank')`) and assign `tab.location` once
 * this promise resolves. Because the opened window is opened with
 * `noopener`, `window.open`'s return value is always `null` here regardless
 * of whether the tab was blocked, so blocking cannot be detected from inside
 * this helper.
 *
 * @param {string} redirect - portal path to land on, e.g. '/my-courses'
 * @param {string|null} fallbackRedirect - path to use if the mint fails
 * @param {Object} options
 * @param {boolean} options.newTab - open in a new tab instead of navigating
 * @returns {Promise<void>}
 */
export default async function handleRedirect(redirect, fallbackRedirect = null, options = {}) {
    const { newTab = false } = options;
    const fallback = fallbackRedirect || redirect || '/';

    try {
        const userId = await resolveUserId();

        if (!userId) {
            throw new Error('No stored userId to mint a learn portal handoff for');
        }

        const data = await mintHandoff(userId, redirect);

        if (!data || !data.url) {
            throw new Error('Learn portal handoff response contained no url');
        }

        navigate(data.url, newTab);
    } catch (error) {
        /*
         * Every distinct failure used to land here indistinguishably — the
         * catch did not even bind the error — so a learner arriving at the
         * portal's login with `token=null` said nothing about WHY. It is the
         * same symptom whether there was no stored session, the mint 403'd, or
         * core was unreachable, and the fallback below is a full-page
         * navigation, so whatever the console held is gone a moment later.
         *
         * This does not change the outcome, only its legibility.
         */
        console.warn('Learn portal handoff failed; falling back to the portal login.', {
            reason: describeFailure(error),
            hasToken: !! window.localStorage.getItem('jwtToken'),
            userId: window.localStorage.getItem('userId'),
            coreUrl: (window.LaraVue.core && window.LaraVue.core.url) || '(not configured)',
        });

        const portalUrl = (window.LaraVue.learn && window.LaraVue.learn.portalUrl) || '';

        if (!portalUrl) {
            console.warn('LEARN_PORTAL_URL is not configured; cannot reach the learner portal.');

            return;
        }

        navigate(`${portalUrl}/auth/dispatch?token=null&redirect=${encodeURIComponent(fallback)}`, newTab);
    }
}

/**
 * Asks core for the handoff URL. Core refuses a user with no learner row
 * (`learner.profileMissing`), and this site's registrations do not write one —
 * the observers' syncs are queued — so a member handed off straight after
 * paying would hit that. Have the website create the row now, then try once more.
 */
async function mintHandoff(userId, redirect) {
    const mint = () => window.coreApiClient.post(`/v1/users/${userId}/accesses/learn-portal`, { redirect });

    try {
        return (await mint()).data;
    } catch (error) {
        const message = error && error.response && error.response.data && error.response.data.message;

        if (message !== 'learner.profileMissing') {
            throw error;
        }

        await window.axios.post('/api/auth/learner-sync');

        return (await mint()).data;
    }
}

/**
 * The stored userId, or — when a session token exists without one — the id
 * from the auth-user fetch. The legacy endpoints (promo join and renew,
 * affiliate, extend) answer with a token only, and their forms hand off
 * straight away, before anything has run the auth-user fetch that normally
 * stores the id; without this the new member landed on the portal login,
 * signed out, right after paying. Same endpoint and same write as the auth
 * store's getAuthUser, called directly because store/auth imports this module.
 */
async function resolveUserId() {
    const stored = storedUserId();

    if (stored || !jwt.getToken()) {
        return stored;
    }

    const { data } = await window.axios.get(getAuthUser);

    if (data && data.data && data.data.realId) {
        window.localStorage.setItem('userId', data.data.realId);
    }

    return storedUserId();
}

/**
 * Reads through the same guard as utils/session.js: values written with
 * JSON.stringify can reach storage as the literal text "null"/"undefined".
 */
function storedUserId() {
    const value = window.localStorage.getItem('userId');

    if (!value || value === 'null' || value === 'undefined') {
        return null;
    }

    return value.replace(/"/g, '');
}

/**
 * The shortest true statement about why the handoff did not complete.
 *
 * An axios rejection carries the status and core's own `message` (a dotted code
 * like `learner.profileMissing`); a rejection with a request but no response is
 * a network or CORS failure, which is a different problem from a refusal; and
 * anything else is one of the guards above.
 */
function describeFailure(error) {
    if (error && error.response) {
        const message = error.response.data && error.response.data.message;

        return `core responded ${error.response.status}${message ? ` (${message})` : ''}`;
    }

    if (error && error.request) {
        return 'no response from core (network, CORS, or wrong CORE_API_URL)';
    }

    return (error && error.message) || 'unknown';
}

function navigate(url, newTab) {
    if (newTab) {
        window.open(url, '_blank', 'noopener,noreferrer');

        return;
    }

    window.location.replace(url);
}
